
import React from "react";
import { Schemes } from "../../utils";
import LoadingGif from "../../assets/images/loading_circle.gif";
import { Alignment, Classname, FileInput, FileInputManageRef, ObjectHelper, FileInputMountHandler, FileInputValidationEventHandler, FileInputItemTemplateHandler, FileInputAttributesRelays, FileInputPreviewType } from "@rivtn/noseur";

export interface MediaInputManageRef {
    select: () => void;
    remove: () => void;
    files: () => File[];
    generateWithAi: () => void;
    changeFiles: (files: File[]) => void;
}

export interface MediaInputProps {
    accepts?: string;
    default?: string;
    loading?: boolean;
    multiple?: boolean;
    noControl?: boolean;
    maxFileSize?: number;
    instruction?: string;
    concatNewFile?: boolean;
    width?: number | string;
    stickyPreview?: boolean;
    noInstruction?: boolean;
    noFillTemplate?: boolean;
    height?: number | string;
    maxWidth?: number | string;
    style?: React.CSSProperties;
    onRemove?: () => Promise<void>;
    onMount?: FileInputMountHandler;
    previewType?: FileInputPreviewType;
    extraControls?: React.ReactElement;
    relayToAttachFileInputOnly?: boolean;
    attrsRelay?: FileInputAttributesRelays;
    onGenerateWithAi?: () => Promise<File>;
    onSelectFiles?: (files: File[]) => void;
    itemTemplate?: FileInputItemTemplateHandler;
    onValidationFail?: FileInputValidationEventHandler;
    manageRef?: React.ForwardedRef<MediaInputManageRef>;
    fileManageRef?: React.RefObject<FileInputManageRef | null>;
    attachFileInputManageRefManageRef?: React.RefObject<FileInputManageRef | null>;
}

function MediaInput(props: MediaInputProps) {

    const propsStyle = props.style ?? {};
    const fileInputManageRef = React.useRef<FileInputManageRef>(null);
    const [loading, setLoading] = React.useState<boolean>(props.loading ?? false);
    const [defaultSrc, setDefaultSrc] = React.useState<string>(props.default ?? "");
    if (props.width) {
        let mainWidth = props.width;
        if (mainWidth === "100%") mainWidth = "calc(100% - 2px)";
        propsStyle.width = mainWidth;
        if (!props.maxWidth) propsStyle.minWidth = mainWidth;
    }
    if (props.height) {
        propsStyle.height = props.height
        propsStyle.minHeight = props.height
    }
    if (props.maxWidth) {
        propsStyle.maxWidth = props.maxWidth;
    }

    return (<FileInput controlIsManaged stickyPreview={props.stickyPreview} multiple={props.multiple} className="minput" style={{ marginBottom: 10, ...propsStyle }} scheme={Schemes.OMNEHOST} accepts={props.accepts ?? "image/*"} 
        manageRef={(r) => {
            ObjectHelper.resolveRef(fileInputManageRef, r);
            if (props.fileManageRef) ObjectHelper.resolveRef(props.fileManageRef, r);
        }}
        onMount={(onSelect?: (e?: any) => void, onDrop?: React.DragEventHandler<any>, onDragOver?: React.DragEventHandler<any>) => {
            props.onMount?.(onSelect, onDrop, onDragOver);
            if (props.manageRef) {
                ObjectHelper.resolveRef(props.manageRef, {
                    files,
                    select,
                    remove,
                    generateWithAi,
                    changeFiles: (files: File[]) => {
                        fileInputManageRef.current?.changeFiles(files);
                    }
                });
            }
        }} 
        previewType={props.previewType}
        itemTemplate={props.itemTemplate}
        emptyTemplate={buildEmptyTemplate}
        concatNewFile={props.concatNewFile}
        relayToAttachFileInputOnly={props.relayToAttachFileInputOnly}
        attachFileInputManageRef={props.attachFileInputManageRefManageRef}
        onValidationFail={props.onValidationFail} maxFileSize={props.maxFileSize}
        attrsRelay={{ control: { alignment: Alignment.BOTTOM_RIGHT }, ...(props.attrsRelay ?? {}) }}
        onSelectFiles={props.onSelectFiles} control={!props.noControl
            ? <>
                {props.onGenerateWithAi && (<div className={Classname.build("noseur-file-input-label", { "contrast": props.instruction })} onClick={generateWithAi}><i className='fa fa-snowflake' /></div>)}
                <div className={Classname.build("noseur-file-input-label", { "contrast": props.instruction })} onClick={select}><i className='fa fa-pen' /></div>
                {props.onRemove && (<div className={Classname.build("noseur-file-input-label", { "contrast": props.instruction })} onClick={remove}><i className='fa fa-times' /></div>)}
                {props.extraControls}
            </>
            : undefined} />);

    function buildEmptyTemplate(select: ((e?: any) => void) | undefined) {
        if (loading) {
            const loadingComponent = (<div className={Classname.build("media-input-loading", { "filled": props.instruction })}>
                <img src={LoadingGif} alt={"loading ai media generation"} />
            </div>);

            if (props.itemTemplate) {
                return props.itemTemplate({
                    index: -1,
                    url: LoadingGif,
                    file: loadingComponent,
                } as any);
            }
            return loadingComponent;
        }
        if (props.noFillTemplate && props.noInstruction) return;
        if (/*!props.instruction || */defaultSrc) {
            return (<img onClick={select} alt="omnehost media input" style={{ width: (props.width ?? 150), height: (props.height ?? 150) }} src={defaultSrc} />);
        }
        return (<div onClick={select} className={Classname.build("media-template", { "filled-media-template": !props.noFillTemplate })} style={{ width: (props.width ?? 150), height: (props.height ?? 150) }}>
            <div>
                <span>{props.instruction}</span>
            </div>
        </div>)
    }

    function files() {
        return fileInputManageRef.current?.files() ?? [];
    }

    function select() {
        fileInputManageRef.current?.select();
    }

    async function remove() {
        setDefaultSrc("");
        fileInputManageRef.current?.clear();
        await props.onRemove?.()!
    }

    async function generateWithAi() {
        setLoading(true);
        fileInputManageRef.current?.clear();
        return new Promise((resolve, reject) => {
            setTimeout(async () => {
                fileInputManageRef.current?.changeFiles([await props.onGenerateWithAi?.()!]);
                setLoading(false);
                resolve(true);
            }, 5000);
        });

    }

}

export default MediaInput;
